import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { LienService } from './lien-service';
import { LinkFormattingServiceHolder } from './link-formatting-service-holder';

@Injectable({ providedIn: 'root' })
export class LinkFormattingService {
  private readonly httpsUrlRegex = /https:\/\/[^\s<>"']+/gi;
  private readonly verifiedUrls = new Set<string>();
  private readonly pendingUrls = new Set<string>();

  constructor(
    private lienService: LienService,
    private messageService: MessageService
  ) {
    LinkFormattingServiceHolder.service = this;
  }

  /**
   * Formats text for read-only display with clickable links and Open Graph previews.
   */
  public async formatForDisplay(texte: string, estHtml = false): Promise<string> {
    if (!texte) {
      return '';
    }

    const normalized = this.normalizeDisplayInput(texte, estHtml);

    if (this.isAlreadyFormattedHtml(normalized)) {
      return normalized;
    }

    const syncFormatted = !estHtml && !this.looksLikeHtml(normalized)
      ? this.formatPlainTextLinksSync(normalized)
      : null;

    const apiFormatted = await this.formatTexte(
      normalized,
      estHtml || this.looksLikeHtml(normalized),
      true,
      false
    );

    return this.preferDisplayFormat(normalized, apiFormatted, syncFormatted, estHtml);
  }

  /**
   * Formats HTTPS URLs inside HTML while preserving existing tags (fallback when API fails).
   */
  public formatHtmlLinksSync(texte: string | null | undefined): string {
    if (!texte) {
      return '';
    }

    if (this.isAlreadyFormattedHtml(texte)) {
      return texte;
    }

    let result = texte;
    for (const url of this.extractHttpsUrls(texte)) {
      if (this.isUrlAlreadyFormatted(result, url)) {
        continue;
      }

      const linkHtml = `<a href="${this.escapeAttribute(url)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(url)}</a>`;
      const pattern = new RegExp(`(?<![="'=])${this.escapeRegex(url)}`, 'i');
      result = result.replace(pattern, linkHtml);
    }

    return result;
  }

  /**
   * Synchronous client-side formatting for plain text display (links only, no OG previews).
   * Use in tables and lists to avoid escaped HTML without API calls per cell.
   */
  public formatPlainTextLinksSync(texte: string | null | undefined): string {
    if (!texte) {
      return '';
    }

    const normalized = this.normalizeDisplayInput(texte, false);
    if (this.isAlreadyFormattedHtml(normalized)) {
      return normalized;
    }

    let result = '';
    let lastIndex = 0;
    const regex = /https:\/\/[^\s<>"']+/gi;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(normalized)) !== null) {
      result += this.escapeHtml(normalized.slice(lastIndex, match.index)).replace(/\n/g, '<br/>');
      const cleanUrl = this.cleanUrl(match[0]);
      result += `<a href="${this.escapeAttribute(cleanUrl)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(cleanUrl)}</a>`;
      lastIndex = match.index + match[0].length;
    }

    result += this.escapeHtml(normalized.slice(lastIndex)).replace(/\n/g, '<br/>');

    return result;
  }

  /**
   * Returns true when the text already contains formatted HTTPS links.
   */
  public hasFormattedLinks(texte: string | null | undefined): boolean {
    return !!texte && this.isAlreadyFormattedHtml(texte);
  }

  /**
   * Sanitizes plain text or formats HTML before persisting to the database.
   * Plain text fields stay plain text; only malicious links are removed.
   */
  public async formatForSubmit(texte: string, estHtml: boolean): Promise<string> {
    if (!texte) {
      return texte;
    }

    const isHtml = estHtml || this.looksLikeHtml(texte);
    const normalized = isHtml ? texte : this.stripHtmlLinksToPlainText(texte);

    if (this.extractHttpsUrls(normalized).length === 0) {
      return normalized;
    }

    return this.formatTexte(normalized, isHtml, isHtml, true);
  }

  /**
   * Asynchronously verifies URLs when written or pasted in the app.
   */
  public async processNewUrls(texte: string, estHtml: boolean): Promise<{ texte: string; modified: boolean }> {
    const urls = this.extractHttpsUrls(texte);
    if (urls.length === 0) {
      return { texte, modified: false };
    }

    let modified = false;
    let updatedText = texte;

    for (const url of urls) {
      if (this.verifiedUrls.has(url) || this.pendingUrls.has(url)) {
        continue;
      }

      this.pendingUrls.add(url);

      try {
        const response = await firstValueFrom(this.lienService.VerifierLien({
          url,
          inclureOpenGraph: false,
          echecSecurise: true
        }));
        const result = response.body?.valeur;

        if (result?.estMalveillant || response.body?.estUnSucces === false) {
          updatedText = this.removeUrl(updatedText, url);
          modified = true;
          this.messageService.add({
            severity: 'error',
            summary: 'Lien malveillant',
            detail: 'Ce lien a été identifié comme malveillant et a été retiré.',
            life: 5000
          });
          continue;
        }

        this.verifiedUrls.add(url);

        if (estHtml && !this.isUrlAlreadyFormatted(updatedText, url)) {
          const formatted = await this.formatForDisplay(updatedText, true);
          if (formatted !== updatedText) {
            updatedText = formatted;
            modified = true;
          }
        }
      } catch {
        updatedText = this.removeUrl(updatedText, url);
        modified = true;
        this.messageService.add({
          severity: 'error',
          summary: 'Vérification indisponible',
          detail: 'Le lien a été retiré car la vérification est temporairement indisponible.',
          life: 5000
        });
      } finally {
        this.pendingUrls.delete(url);
      }
    }

    return { texte: updatedText, modified };
  }

  public extractHttpsUrls(texte: string): string[] {
    const matches = texte.match(this.httpsUrlRegex) ?? [];
    return [...new Set(matches.map(url => this.cleanUrl(url)))];
  }

  private async formatTexte(
    texte: string,
    estHtml: boolean,
    genererHtml: boolean,
    notifyUser = true
  ): Promise<string> {
    try {
      const response = await firstValueFrom(this.lienService.FormaterTexte({
        texte,
        estHtml,
        genererHtml
      }));
      const result = response.body?.valeur;
      const formatted = result?.texteFormate ?? (result as { TexteFormate?: string } | undefined)?.TexteFormate;

      if (notifyUser && result?.liensMalveillantsRetires?.length) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Lien retiré',
          detail: 'Un ou plusieurs liens malveillants ont été retirés.',
          life: 5000
        });
      }

      if (formatted) {
        return this.stripInvalidPreviews(formatted);
      }

      return genererHtml
        ? (estHtml || this.looksLikeHtml(texte)
          ? this.formatHtmlLinksSync(texte)
          : this.formatPlainTextLinksSync(texte))
        : texte;
    } catch {
      if (genererHtml) {
        return estHtml || this.looksLikeHtml(texte)
          ? this.formatHtmlLinksSync(texte)
          : this.formatPlainTextLinksSync(texte);
      }

      return this.stripAllHttpsUrls(texte);
    }
  }

  private preferDisplayFormat(
    original: string,
    apiFormatted: string,
    syncFormatted: string | null,
    estHtml: boolean
  ): string {
    if (this.isDisplayFormatted(apiFormatted, estHtml)) {
      return apiFormatted;
    }

    if (syncFormatted && this.isDisplayFormatted(syncFormatted, false)) {
      return syncFormatted;
    }

    if (this.extractHttpsUrls(original).length > 0) {
      return estHtml || this.looksLikeHtml(original)
        ? this.formatHtmlLinksSync(original)
        : this.formatPlainTextLinksSync(original);
    }

    return apiFormatted || syncFormatted || original;
  }

  private isDisplayFormatted(texte: string, _estHtml: boolean): boolean {
    return this.isAlreadyFormattedHtml(texte);
  }

  private normalizeDisplayInput(texte: string, estHtml: boolean): string {
    if (estHtml || !this.containsEncodedHtml(texte)) {
      return texte;
    }

    return this.decodeHtmlEntities(texte);
  }

  private stripHtmlLinksToPlainText(texte: string): string {
    return texte
      .replace(/<a\b[^>]*href=["'](https:\/\/[^"']+)["'][^>]*>[\s\S]*?<\/a>/gi, '$1')
      .replace(/<a\b[^>]*class=["'][^"']*sihc-link-preview[^"']*["'][^>]*>[\s\S]*?<\/a>/gi, '')
      .replace(/<div\b[^>]*class=["'][^"']*sihc-link-preview[^"']*["'][^>]*>[\s\S]*?<\/div>/gi, '')
      .replace(/<br\s*\/?>/gi, '\n');
  }

  private looksLikeHtml(texte: string): boolean {
    return /<\/?[a-z][\s\S]*>/i.test(texte);
  }

  private isAlreadyFormattedHtml(texte: string): boolean {
    return texte.includes('sihc-link-preview')
      || /href=["']https:\/\//i.test(texte);
  }

  private containsEncodedHtml(texte: string): boolean {
    return /&lt;\/?[a-z]/i.test(texte) || /&quot;https:\/\//i.test(texte);
  }

  private decodeHtmlEntities(texte: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = texte;
    return textarea.value;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private escapeAttribute(value: string): string {
    return value.replace(/"/g, '&quot;');
  }

  private escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private cleanUrl(url: string): string {
    return url.replace(/[.,;:)>\]"']+$/, '');
  }

  private removeUrl(texte: string, url: string): string {
    return texte.split(url).join('');
  }

  private stripAllHttpsUrls(texte: string): string {
    let result = texte;
    for (const url of this.extractHttpsUrls(texte)) {
      result = this.removeUrl(result, url);
    }
    return result;
  }

  private isUrlAlreadyFormatted(texte: string, url: string): boolean {
    return texte.includes(`href="${url}"`)
      || texte.includes(`data-sihc-link-url="${url}"`)
      || texte.includes(`data-url="${url}"`);
  }

  /**
   * Removes preview cards when the formatted HTML has no usable Open Graph content.
   */
  private stripInvalidPreviews(html: string): string {
    if (!html.includes('sihc-link-preview')) {
      return html;
    }

    return html.replace(
      /<a\b[^>]*class=["'][^"']*sihc-link-preview[^"']*["'][^>]*>[\s\S]*?<\/a>/gi,
      (previewBlock) => {
        const hasTitle = previewBlock.includes('sihc-link-preview-title');
        const hasDescription = previewBlock.includes('sihc-link-preview-description');
        const hasImage = previewBlock.includes('sihc-link-preview-image');
        return hasTitle || hasDescription || hasImage ? previewBlock : '';
      }
    ).replace(
      /<div\b[^>]*class=["'][^"']*sihc-link-preview[^"']*["'][^>]*>[\s\S]*?<\/div>/gi,
      (previewBlock) => {
        const hasTitle = previewBlock.includes('sihc-link-preview-title');
        const hasDescription = previewBlock.includes('sihc-link-preview-description');
        const hasImage = previewBlock.includes('sihc-link-preview-image');
        return hasTitle || hasDescription || hasImage ? previewBlock : '';
      }
    );
  }
}
